import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { authGoogleButtonVisualSx } from './auth/authFormStyles';
import { GoogleGLogo } from './auth/GoogleGLogo';
import { GoogleSignInConfigError } from '../utils/googleSignInErrors';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (element: HTMLElement, config: {
            type: string;
            theme: string;
            size: string;
            text: string;
            width?: number;
            shape?: 'rectangular' | 'pill' | 'circle' | 'square';
          }) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface GoogleSignInButtonProps {
  onSuccess: (credential: string) => void;
  onError?: (error: Error) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  /** Fallback width before layout measure; Google GIS requires pixels. */
  width?: number;
}

const GOOGLE_BUTTON_LABEL: Record<
  NonNullable<GoogleSignInButtonProps['text']>,
  string
> = {
  signin_with: 'Sign in with Google',
  signup_with: 'Sign up with Google',
  continue_with: 'Continue with Google',
  signin: 'Sign in with Google',
};

function GoogleSignInButtonVisual({
  label,
  onActivate,
}: {
  label: string;
  onActivate?: () => void;
}) {
  const interactive = Boolean(onActivate);

  return (
    <Box
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? label : undefined}
      onClick={onActivate}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate?.();
              }
            }
          : undefined
      }
      sx={{
        ...authGoogleButtonVisualSx,
        width: '100%',
        cursor: interactive ? 'pointer' : 'default',
      }}
    >
      <GoogleGLogo size={18} />
      <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccess,
  onError,
  text = 'signin_with',
  width = 300,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const [renderWidth, setRenderWidth] = useState(width);

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim();
  const label = GOOGLE_BUTTON_LABEL[text];

  const notifyConfigError = () => {
    onErrorRef.current?.(new GoogleSignInConfigError());
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !googleClientId) return;

    const updateWidth = () => {
      const next = Math.floor(el.getBoundingClientRect().width);
      if (next > 0) {
        setRenderWidth((prev) => (prev === next ? prev : next));
      }
    };

    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [googleClientId]);

  useEffect(() => {
    if (!googleClientId) {
      return;
    }

    let isMounted = true;
    let checkInterval: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const initializeGoogleSignIn = () => {
      if (!isMounted) return;
      if (!window.google?.accounts) {
        console.warn('Google Identity Services not loaded');
        onErrorRef.current?.(new Error('Google Identity Services failed to load'));
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => {
            onSuccessRef.current(response.credential);
          },
        });

        if (buttonRef.current && isMounted) {
          buttonRef.current.innerHTML = '';
          window.google.accounts.id.renderButton(buttonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: text,
            width: renderWidth,
            shape: 'rectangular',
          });
        }
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
        onErrorRef.current?.(error as Error);
      }
    };

    if (window.google?.accounts) {
      initializeGoogleSignIn();
    } else {
      checkInterval = setInterval(() => {
        if (window.google?.accounts) {
          if (checkInterval) clearInterval(checkInterval);
          if (timeoutId) clearTimeout(timeoutId);
          initializeGoogleSignIn();
        }
      }, 100);

      timeoutId = setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
        if (!window.google?.accounts && isMounted) {
          onErrorRef.current?.(new Error('Google Identity Services failed to load'));
        }
      }, 5000);
    }

    return () => {
      isMounted = false;
      if (checkInterval) clearInterval(checkInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [googleClientId, text, renderWidth]);

  if (!googleClientId) {
    return <GoogleSignInButtonVisual label={label} onActivate={notifyConfigError} />;
  }

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: 44 }}>
      <Box aria-hidden sx={{ ...authGoogleButtonVisualSx, position: 'absolute', inset: 0 }}>
        <GoogleGLogo size={18} />
        <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
          {label}
        </Typography>
      </Box>
      <Box
        ref={buttonRef}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          opacity: 0,
          cursor: 'pointer',
          overflow: 'hidden',
          '& > div': { width: '100%', height: '100%' },
          '& iframe': {
            width: '100% !important',
            height: '44px !important',
            margin: '0 !important',
          },
        }}
      />
    </Box>
  );
};
